import React, { useEffect, useMemo, useState } from "react";
import useApi from "../../CustomHooks/useApi";
import { gearViewAPI } from "../../../api/apiClient";
import LoadingOverlay from "../../../LoadingOverlay";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSyncAlt, faKitMedical, faFlag, faBroadcastTower, faCompass, faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { useUser } from "../../userContext";
import UpdateConsumablesModal from "./UpdateConsumablesModal";
import "../../../css/Views/GearView.css";
import TransferSimpleModal from "./TransferSimpleModal";
import HistoryModal from "./HistoryModal";
import { faArrowRightArrowLeft } from "@fortawesome/free-solid-svg-icons";
import TransferConsumablesModalBulk from "./TransferConsumablesModalBulk";
import Loading from "../../../Loading";
import { useToast } from "../../../Contexts/ToastContext";


const LABELS = {
  firstAidKits: "Apteczki",
  greenFlag: "Flagi zielone",
  rainbowFlag: "Flagi tęczowe",
  flagPoles: "Maszty do flag",
  walkieTalkies: "Krótkofalówki",
  compasses: "Kompasy",
};

const ENUM_VALUE = {
  firstAidKits: 1,
  greenFlag: 2,
  rainbowFlag: 3,
  walkieTalkies: 4,
  compasses: 5,
  flagPoles: 6,
};

const EXPECTED_TOTALS = {
  firstAidKits: 3,
  greenFlag: 4,
  rainbowFlag: 2,
  flagPoles: 3,
  walkieTalkies: 6,
  compasses: 12,
};

const GearsView = () => {
  const { callApi, isLoading, error } = useApi();
  const { user } = useUser();
  const { showToast } = useToast();
  const meId = user?.id ?? null;
  const [rows, setRows] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferCtx, setTransferCtx] = useState({ gearKey: null, label: "", maxCount: 0 });
  const [transferConsumablesOpen, setTransferConsumablesOpen] = useState(false);
  const [isUpdatingMySection, setIsUpdatingMySection] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const [historyOpen, setHistoryOpen] = useState(false);

useEffect(() => {
  const fetchData = async () => {
    setIsInitialLoading(true);
    await callApi(gearViewAPI.getAll, setRows);
    setIsInitialLoading(false);
  };
  fetchData();
}, [refresh]);

  const myRow = useMemo(() => {
    const found = rows.find((r) => r.userId === meId);
    return (
      found ?? {
        userId: meId ?? -1,
        preferredName: user?.preferredName ?? "Ja",
        stickersLarge: 0,
        stickersSmall: 0,
        patches: 0,
        businessCards: 0,
        pinMetalClasp: 0,
        pinScrewBack: 0,
        pinPlasticClasp: 0,
        pinCards: 0,
        firstAidKits: 0,
        greenFlag: 0,
        rainbowFlag: 0,
        flagPoles: 0,
        walkieTalkies: 0,
        compasses: 0,
      }
    );
  }, [rows, meId, user]);

  const fixedGrouped = useMemo(() => {
    const groups = {
      firstAidKits: [],
      greenFlag: [],
      rainbowFlag: [],
      flagPoles: [],
      walkieTalkies: [],
      compasses: [],
    };

    rows.forEach((r) => {
      if (r.firstAidKits > 0) groups.firstAidKits.push({ name: r.preferredName ?? `Użytkownik #${r.userId}`, count: r.firstAidKits });
      if (r.greenFlag > 0) groups.greenFlag.push({ name: r.preferredName ?? `Użytkownik #${r.userId}`, count: r.greenFlag });
      if (r.rainbowFlag > 0) groups.rainbowFlag.push({ name: r.preferredName ?? `Użytkownik #${r.userId}`, count: r.rainbowFlag });
      if (r.flagPoles > 0) groups.flagPoles.push({ name: r.preferredName ?? `Użytkownik #${r.userId}`, count: r.flagPoles });
      if (r.walkieTalkies > 0) groups.walkieTalkies.push({ name: r.preferredName ?? `Użytkownik #${r.userId}`, count: r.walkieTalkies });
      if (r.compasses > 0) groups.compasses.push({ name: r.preferredName ?? `Użytkownik #${r.userId}`, count: r.compasses });
    });

    return groups;
  }, [rows]);

  const getFixedTotal = (gearKey) => {
    return rows.reduce((sum, r) => sum + (r?.[gearKey] ?? 0), 0);
  };

  const handleRefresh = () => setRefresh((x) => x + 1);

  const handleSubmitModal = async (payload) => {
    setIsModalOpen(false);
    await callApi(() => gearViewAPI.updateConsumables(payload), () => {});
    setIsModalOpen(false);
    handleRefresh();
  };

  const recipients = useMemo(
    () => rows.filter((r) => r.userId !== meId).sort((a, b) => (a.preferredName ?? "").localeCompare(b.preferredName ?? "")),
    [rows, meId]
  );

  const openTransfer = (gearKey) => {
    const maxCount = myRow?.[gearKey] ?? 0;
    if (maxCount < 1) return;
    setTransferCtx({ gearKey, label: LABELS[gearKey], maxCount });
    setTransferOpen(true);
  };

  const handleTransferSubmit = async ({ recipientId, count, note }) => {
    setTransferOpen(false);
    await callApi(
      () =>
        gearViewAPI.transfer({
          toUserId: recipientId,
          item: ENUM_VALUE[transferCtx.gearKey],
          quantity: count,
          note,
        }),
      () => {}
    );
    handleRefresh();
  };

  const totals = useMemo(() => {
    const acc = {
      stickersLarge: 0,
      stickersSmall: 0,
      patches: 0,
      businessCards: 0,
      pinMetalClasp: 0,
      pinScrewBack: 0,
      pinPlasticClasp: 0,
      pinCards: 0,
    };
    rows.forEach(r => {
      acc.stickersLarge   += r.stickersLarge   ?? 0;
      acc.stickersSmall   += r.stickersSmall   ?? 0;
      acc.patches         += r.patches         ?? 0;
      acc.businessCards   += r.businessCards   ?? 0;
      acc.pinMetalClasp   += r.pinMetalClasp   ?? 0;
      acc.pinScrewBack    += r.pinScrewBack    ?? 0;
      acc.pinPlasticClasp += r.pinPlasticClasp ?? 0;
      acc.pinCards        += r.pinCards        ?? 0;
    });
    return acc;
  }, [rows]);

const handleTransferConsumable = async ({ recipientId, transfers, note, onDone }) => {
  try {
    await callApi(() =>
      gearViewAPI.transferConsumables({
        toUserId: recipientId,
        transfers,
        note,
      })
    );

    showToast("Przekazano zasoby!", "success");
    onDone(true);

    setIsUpdatingMySection(true);
    await handleRefresh();
  } catch (err) {
    console.error(err);
    onDone(false);
  } finally {
    setIsUpdatingMySection(false);
  }
};


  return (
    <div className="gear-view">
      {isInitialLoading && <LoadingOverlay isLoading={true} />}

      <div className="gear-header-section">
        <div className="gear-header">
          Zasoby
          <FontAwesomeIcon icon={faSyncAlt} className="refresh-icon" onClick={handleRefresh} title="Odśwież widok" />
        </div>
      </div>
      <div className="my-bar">
<div className="my-bar-top">
  <div className="my-left">
    <div className="my-label">{myRow.preferredName ?? "Ja"}</div>
    {!!meId && (
 <div className="gear-actions" style={{ display: "flex", gap: 10, alignItems: "center" }}>
      <div
        className="gear-edit-icon"
        onClick={() => setIsModalOpen(true)}
        title="Aktualizuj moje"
        role="button"
        tabIndex={0}
      >
        <FontAwesomeIcon icon={faPenToSquare} />
      </div>
      <div
        className="gear-edit-icon"
        onClick={() => setTransferConsumablesOpen(true)}
        title="Przekaż zasoby"
      >
        <FontAwesomeIcon icon={faArrowRightArrowLeft} />
      </div>

    </div>

      
    )}

    
  </div>

        <div
          className="gear-secondary-btn history-btn"
          role="button"
          tabIndex={0}
          onClick={() => setHistoryOpen(true)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setHistoryOpen(true)}
          title="Pokaż historię"
        >
          Historia
        </div>

        </div>


        {isUpdatingMySection ? <Loading text="Ładowanie danych"/> : <div className="my-values">
          <span>Naklejki duże: {myRow.stickersLarge}</span>
          <span>Naklejki małe: {myRow.stickersSmall}</span>
          <span>Naszywki: {myRow.patches}</span>
          <span>Wizytówki: {myRow.businessCards}</span>
          <span>Pin (metal): {myRow.pinMetalClasp}</span>
          <span>Pin (wkrętka): {myRow.pinScrewBack}</span>
          <span>Pin (plastik): {myRow.pinPlasticClasp}</span>
          <span>Kartoniki do pinów: {myRow.pinCards}</span>
        </div>
        }
        </div>

      <section className="gear-section">
        <div className="fixed-list">
          {Object.entries(fixedGrouped).map(([gearKey, owners]) => {
            const iHave = (myRow?.[gearKey] ?? 0) > 0;
            const currentTotal = getFixedTotal(gearKey);
            const expectedTotal = EXPECTED_TOTALS[gearKey];
            const mismatch = expectedTotal != null && currentTotal !== expectedTotal;

            return (
              <div key={gearKey} className="gear-block">
                <div className="gear-label" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {gearKey === "firstAidKits" && <><FontAwesomeIcon icon={faKitMedical} /> {LABELS[gearKey]}</>}
                  {gearKey === "greenFlag" && <><FontAwesomeIcon icon={faFlag} style={{ color: "green" }} /> {LABELS[gearKey]}</>}
                  {gearKey === "rainbowFlag" && <><FontAwesomeIcon icon={faFlag} style={{ color: "purple" }} /> {LABELS[gearKey]}</>}
                  {gearKey === "flagPoles" && <><FontAwesomeIcon icon={faFlag} style={{ color: "gray" }} /> {LABELS[gearKey]}</>}
                  {gearKey === "walkieTalkies" && <><FontAwesomeIcon icon={faBroadcastTower} /> {LABELS[gearKey]}</>}
                  {gearKey === "compasses" && <><FontAwesomeIcon icon={faCompass} /> {LABELS[gearKey]}</>}

                  <span className={`fixed-expected ${mismatch ? "mismatch" : ""}`} title="aktualnie / faktycznie">
                    ({currentTotal}/{expectedTotal ?? "?"})
                  </span>

                  {iHave && (
                    <div className="transfer-btn" onClick={() => openTransfer(gearKey)}>
                      Przekaż
                    </div>
                  )}
                </div>

                <ul>
                  {owners.map((o, idx) => (
                    <li key={idx}>
                      {o.name} <span className="count">(x{o.count})</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      <section className="gear-section">
        <div className="totals-bar">
          <div className="totals-title">Łączne zasoby:</div>
          <div className="totals-values">
            <span>Naklejki duże: {totals.stickersLarge}</span>
            <span>Naklejki małe: {totals.stickersSmall}</span>
            <span>Naszywki: {totals.patches}</span>
            <span>Wizytówki: {totals.businessCards}</span>
            <span>Pin (metal): {totals.pinMetalClasp}</span>
            <span>Pin (wkrętka): {totals.pinScrewBack}</span>
            <span>Pin (plastik): {totals.pinPlasticClasp}</span>
            <span>Kartoniki do pinów: {totals.pinCards}</span>
          </div>
        </div>
      </section>

      <section className="gear-section">
        <div className="table-wrap">
          <table className="gear-table">
            <thead>
              <tr>
                <th>Osoba</th>
                <th>Naklejki duże</th>
                <th>Naklejki małe</th>
                <th>Naszywki</th>
                <th>Wizytówki</th>
                <th>Pin (metal)</th>
                <th>Pin (wkrętka)</th>
                <th>Pin (plastik)</th>
                <th>Kartoniki do pinów</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.userId} className={r.userId === meId ? "me" : undefined}>
                  <td>{r.preferredName ?? `Użytkownik #${r.userId}`}</td>
                  <td>{r.stickersLarge}</td>
                  <td>{r.stickersSmall}</td>
                  <td>{r.patches}</td>
                  <td>{r.businessCards}</td>
                  <td>{r.pinMetalClasp}</td>
                  <td>{r.pinScrewBack}</td>
                  <td>{r.pinPlasticClasp}</td>
                  <td>{r.pinCards}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <UpdateConsumablesModal
        isOpen={isModalOpen}
        initial={{
          stickersLarge: myRow.stickersLarge,
          stickersSmall: myRow.stickersSmall,
          patches: myRow.patches,
          businessCards: myRow.businessCards,
          pinMetalClasp: myRow.pinMetalClasp,
          pinScrewBack: myRow.pinScrewBack,
          pinPlasticClasp: myRow.pinPlasticClasp,
          pinCards: myRow.pinCards,
        }}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmitModal}
      />

      <TransferSimpleModal
        isOpen={transferOpen}
        gearLabel={transferCtx.label}
        maxCount={transferCtx.maxCount}
        recipients={recipients}
        onClose={() => setTransferOpen(false)}
        onSubmit={handleTransferSubmit}
      />

      <TransferConsumablesModalBulk
        isOpen={transferConsumablesOpen}
        myRow={myRow}
        recipients={recipients}
        onClose={() => setTransferConsumablesOpen(false)}
        onSubmit={handleTransferConsumable}
      />


      <HistoryModal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </div>
  );
};

export default GearsView;
